import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Undo2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * <Unsubscribe /> — public one-click opt-out landing page.
 *
 * Every drip email footer embeds `/unsubscribe?e=<email>&t=<hmac>`.
 * We hit `POST /api/retention/unsubscribe` on mount, show a clean
 * confirmation, and expose an "Actually, I want to stay" resubscribe
 * button in case the click was accidental. No login required — the
 * HMAC token proves the click came from a legitimate email.
 */
export default function Unsubscribe() {
    const [state, setState] = useState({ phase: "loading", message: "", email: "", token: "" });
    const location = useLocation();

    useEffect(() => {
        const p = new URLSearchParams(location.search);
        const email = p.get("e") || "";
        const t = p.get("t") || "";
        if (!email || !t) {
            setState({ phase: "err", message: "Missing unsubscribe token. Reply to any Memory Lane email and we'll remove you manually.", email, token: t });
            return;
        }
        (async () => {
            try {
                await api.post("/retention/unsubscribe", null, { params: { email, t } });
                setState({ phase: "ok", message: "", email, token: t });
            } catch (e) {
                setState({
                    phase: "err",
                    message: e?.response?.data?.detail || "This unsubscribe link isn't valid. Reply to any Memory Lane email to remove yourself.",
                    email, token: t,
                });
            }
        })();
    }, [location.search]);

    const resubscribe = async () => {
        setState((s) => ({ ...s, phase: "loading" }));
        try {
            await api.post("/retention/resubscribe", null, { params: { email: state.email, t: state.token } });
            setState((s) => ({ ...s, phase: "resubscribed" }));
        } catch (e) {
            setState((s) => ({ ...s, phase: "err", message: e?.response?.data?.detail || "Couldn't undo — try again." }));
        }
    };

    return (
        <div className="min-h-screen bg-cream-light flex items-center justify-center p-4" data-testid="unsubscribe-page">
            <div className="ml-card p-8 max-w-md w-full text-center">
                <div className="text-teal-dark font-semibold text-lg mb-1">Memory Lane</div>
                <div className="text-xs text-[#6E7A74] uppercase tracking-wider mb-6">Care Navigator</div>

                {state.phase === "loading" && (
                    <>
                        <Loader2 className="animate-spin mx-auto mb-3 text-teal-dark" size={28} strokeWidth={1.8} />
                        <p className="text-[#4C5551]">Processing…</p>
                    </>
                )}

                {state.phase === "ok" && (
                    <>
                        <CheckCircle2 className="mx-auto mb-3 text-stage_early-dark" size={32} strokeWidth={1.8} />
                        <h1 className="text-xl font-heading font-semibold text-[#1F2624] mb-2">You&apos;re unsubscribed.</h1>
                        <p className="text-sm text-[#4C5551] mb-4">
                            <span className="font-mono text-[13px]">{state.email}</span> won&apos;t get any more Memory Lane emails.
                        </p>
                        <p className="text-xs text-[#6E7A74] mb-6 italic">
                            You can still sign in to <a href="https://memorylanecare.net" className="text-teal-dark underline">memorylanecare.net</a> anytime.
                        </p>
                        <button
                            onClick={resubscribe}
                            className="inline-flex items-center gap-1.5 text-sm text-teal-dark hover:text-teal-dark underline underline-offset-2"
                            data-testid="unsubscribe-undo"
                        >
                            <Undo2 size={14} strokeWidth={2} /> Actually, keep me on the list
                        </button>
                    </>
                )}

                {state.phase === "resubscribed" && (
                    <>
                        <CheckCircle2 className="mx-auto mb-3 text-stage_early-dark" size={32} strokeWidth={1.8} />
                        <h1 className="text-xl font-heading font-semibold text-[#1F2624] mb-2">Welcome back.</h1>
                        <p className="text-sm text-[#4C5551]">You&apos;ll keep getting Memory Lane emails.</p>
                    </>
                )}

                {state.phase === "err" && (
                    <>
                        <XCircle className="mx-auto mb-3 text-coral-dark" size={32} strokeWidth={1.8} />
                        <h1 className="text-xl font-heading font-semibold text-[#1F2624] mb-2">Something went wrong</h1>
                        <p className="text-sm text-[#4C5551] mb-4">{state.message}</p>
                        <Link to="/" className="text-teal-dark underline text-sm">Back to Memory Lane</Link>
                    </>
                )}
            </div>
        </div>
    );
}
